export const WIDGET_STYLE_ID = "indexaai-widget-style";

// Cores, raio, fonte e posição vêm como CSS variables no root do widget (ver index.ts), nunca em
// document.documentElement. Os tons derivados seguem o getAssistantAgentPreviewPalette do painel.
// Todas as classes têm prefixo indexaai- e o tema da loja não quebra o layout.
// Movimento só entra com prefers-reduced-motion: no-preference (transform e opacity, sem layout).
const CSS = `
  .indexaai-widget, .indexaai-widget * { box-sizing: border-box; }
  .indexaai-widget {
    --indexaai-subtle: color-mix(in srgb, var(--indexaai-background) 95%, var(--indexaai-text));
    --indexaai-border: color-mix(in srgb, var(--indexaai-background) 82%, var(--indexaai-text));
    --indexaai-muted: color-mix(in srgb, var(--indexaai-text) 62%, var(--indexaai-background));
    --indexaai-accent-bg: color-mix(in srgb, var(--indexaai-primary) 10%, var(--indexaai-background));
    --indexaai-bot-bg: color-mix(in srgb, var(--indexaai-primary) 9%, transparent);
    pointer-events: none; font-family: var(--indexaai-font); color: var(--indexaai-text); line-height: 1.45; font-size: 14px;
  }
  .indexaai-widget :where(button, input) { font: inherit; margin: 0; color: inherit; }
  .indexaai-widget [hidden] { display: none !important; }
  .indexaai-widget svg { display: block; flex-shrink: 0; }
  .indexaai-widget :focus-visible { outline: 2px solid var(--indexaai-primary); outline-offset: 2px; }
  .indexaai-widget button:disabled, .indexaai-widget input:disabled { opacity: 0.5; cursor: default; }
  .indexaai-widget-sr {
    position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden;
    clip: rect(0 0 0 0); white-space: nowrap; border: 0;
  }

  /* Launcher: 56 px, sombra suave, ícone centralizado */
  .indexaai-widget-button {
    position: fixed; bottom: var(--indexaai-offset-y); z-index: 2147483000;
    width: 56px; height: 56px; padding: 0; border: none; border-radius: var(--indexaai-radius); overflow: hidden;
    display: flex; align-items: center; justify-content: center; cursor: pointer; pointer-events: auto;
    background: var(--indexaai-primary); color: var(--indexaai-accent-fg);
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.22);
  }
  .indexaai-widget-button svg { width: 24px; height: 24px; }
  .indexaai-widget-button img { width: 28px; height: 28px; object-fit: contain; }
  .indexaai-widget-button:focus-visible { outline: 3px solid var(--indexaai-primary); outline-offset: 3px; }
  .indexaai-widget[data-anchor="bottom-right"] .indexaai-widget-button { right: var(--indexaai-offset-x); }
  .indexaai-widget[data-anchor="bottom-left"] .indexaai-widget-button { left: var(--indexaai-offset-x); }

  /* Painel: até 380 px de largura e 640 px de altura, sem passar da tela nem do topo */
  .indexaai-widget-panel {
    position: fixed; bottom: var(--indexaai-panel-y); z-index: 2147483001;
    width: min(380px, calc(100vw - 32px));
    max-height: min(640px, calc(100vh - 120px), calc(100vh - var(--indexaai-panel-y) - 16px));
    display: flex; flex-direction: column; overflow: hidden; isolation: isolate; pointer-events: auto;
    background: var(--indexaai-background); color: var(--indexaai-text);
    border: 1px solid var(--indexaai-border); border-radius: var(--indexaai-radius);
    box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  }
  .indexaai-widget[data-anchor="bottom-right"] .indexaai-widget-panel { right: var(--indexaai-panel-x); transform-origin: bottom right; }
  .indexaai-widget[data-anchor="bottom-left"] .indexaai-widget-panel { left: var(--indexaai-panel-x); transform-origin: bottom left; }

  .indexaai-widget-header {
    display: flex; align-items: center; gap: 12px; padding: 16px;
    background: var(--indexaai-primary); color: var(--indexaai-accent-fg); flex-shrink: 0;
  }
  .indexaai-widget-avatar {
    width: 36px; height: 36px; flex-shrink: 0; border-radius: 999px; overflow: hidden;
    background: rgba(255, 255, 255, 0.2); display: flex; align-items: center; justify-content: center;
  }
  .indexaai-widget-avatar svg { width: 20px; height: 20px; }
  .indexaai-widget-avatar img { width: 100%; height: 100%; object-fit: cover; }
  .indexaai-widget-title {
    flex: 1; min-width: 0; font-weight: 600; font-size: 15px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .indexaai-widget-header-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
  .indexaai-widget-header-action {
    width: 32px; height: 32px; padding: 0; border: none; border-radius: 999px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255, 255, 255, 0.16); color: inherit;
  }
  .indexaai-widget-header-action:hover { background: rgba(255, 255, 255, 0.28); }
  .indexaai-widget-header-action svg { width: 17px; height: 17px; }
  .indexaai-widget-close {
    width: 32px; height: 32px; flex-shrink: 0; padding: 0; border: none; border-radius: 999px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255, 255, 255, 0.16); color: inherit;
  }
  .indexaai-widget-close:hover { background: rgba(255, 255, 255, 0.28); }
  .indexaai-widget-close svg { width: 18px; height: 18px; }
  .indexaai-widget-close:focus-visible, .indexaai-widget-header :focus-visible { outline-color: var(--indexaai-accent-fg); }

  .indexaai-widget-messages {
    flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain;
    padding: 16px; display: flex; flex-direction: column; gap: 16px;
  }
  .indexaai-widget-message {
    max-width: 94%; padding: 12px; line-height: 1.45; word-wrap: break-word; white-space: pre-wrap;
    border-radius: var(--indexaai-radius);
  }
  .indexaai-widget-message-copy { display: block; }
  .indexaai-widget-message-time {
    display: block; margin-top: 6px; font-size: 10px; line-height: 1.2; opacity: 0.68; text-align: right;
    white-space: nowrap;
  }
  .indexaai-widget-message--bot { align-self: flex-start; background: var(--indexaai-bot-bg); }
  .indexaai-widget-message--user { align-self: flex-end; background: var(--indexaai-primary); color: var(--indexaai-accent-fg); }
  .indexaai-widget-message--error { align-self: flex-start; background: rgba(185, 28, 28, 0.08); color: #b91c1c; }
  .indexaai-widget-message img { display: block; width: 100%; height: 144px; object-fit: cover; border-radius: 12px; margin-bottom: 8px; }
  .indexaai-widget-caption { display: block; margin-top: 6px; font-size: 12px; opacity: 0.8; }

  .indexaai-widget-typing {
    align-self: flex-start; display: flex; align-items: center; gap: 4px; padding: 12px 14px;
    border-radius: var(--indexaai-radius); background: var(--indexaai-bot-bg);
  }
  .indexaai-widget-dot { width: 6px; height: 6px; border-radius: 999px; background: var(--indexaai-muted); }

  .indexaai-widget-chips { display: flex; flex-wrap: wrap; gap: 8px; padding-top: 4px; }
  .indexaai-widget-chip {
    padding: 6px 12px; border-radius: 999px; cursor: pointer; font-size: 12px; text-align: left; line-height: 1.3;
    border: 1px solid var(--indexaai-primary); background: transparent; color: var(--indexaai-primary);
  }
  .indexaai-widget-chip:hover { background: var(--indexaai-bot-bg); }

  .indexaai-widget-history { display: flex; flex-direction: column; gap: 8px; }
  .indexaai-widget-history-heading { margin: 0 0 4px; font-size: 16px; font-weight: 650; }
  .indexaai-widget-history-note { margin: 0 0 4px; color: var(--indexaai-muted); font-size: 12px; }
  .indexaai-widget-history-item {
    display: flex; align-items: center; gap: 12px; width: 100%; padding: 12px; text-align: left;
    border: 1px solid var(--indexaai-border); border-radius: 12px; cursor: pointer;
    background: var(--indexaai-background); color: var(--indexaai-text);
  }
  .indexaai-widget-history-item:hover { background: var(--indexaai-subtle); }
  .indexaai-widget-history-item-icon {
    width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;
    flex: 0 0 34px; border-radius: 10px; background: var(--indexaai-accent-bg); color: var(--indexaai-primary);
  }
  .indexaai-widget-history-item-icon svg { width: 17px; height: 17px; }
  .indexaai-widget-history-item-copy { min-width: 0; flex: 1; }
  .indexaai-widget-history-item-title { display: block; font-size: 13px; font-weight: 600; }
  .indexaai-widget-history-item-time { display: block; margin-top: 2px; color: var(--indexaai-muted); font-size: 11px; }
  .indexaai-widget-history-empty {
    display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 32px 18px;
    color: var(--indexaai-muted); text-align: center;
  }
  .indexaai-widget-history-empty svg { width: 24px; height: 24px; color: var(--indexaai-primary); }
  .indexaai-widget-feedback { display: flex; flex-direction: column; gap: 12px; }
  .indexaai-widget-feedback-title { margin: 0; font-size: 16px; font-weight: 650; }
  .indexaai-widget-feedback-note { margin: -6px 0 0; color: var(--indexaai-muted); font-size: 12px; }
  .indexaai-widget-feedback-options { display: flex; gap: 8px; }
  .indexaai-widget-feedback-option {
    display: flex; align-items: center; gap: 6px; padding: 8px 11px; border: 1px solid var(--indexaai-border);
    border-radius: 999px; cursor: pointer; background: var(--indexaai-background); color: var(--indexaai-text);
    font-size: 12px;
  }
  .indexaai-widget-feedback-option[aria-pressed="true"] {
    border-color: var(--indexaai-primary); background: var(--indexaai-accent-bg); color: var(--indexaai-primary);
  }
  .indexaai-widget-feedback-textarea {
    width: 100%; min-height: 120px; resize: vertical; padding: 11px 12px; border: 1px solid var(--indexaai-border);
    border-radius: 12px; background: var(--indexaai-subtle); color: inherit; font: inherit; font-size: 13px;
  }
  .indexaai-widget-feedback-submit {
    align-self: flex-end; min-height: 38px; padding: 0 16px; border: 0; border-radius: 999px; cursor: pointer;
    background: var(--indexaai-primary); color: var(--indexaai-accent-fg); font-weight: 600;
  }
  .indexaai-widget-feedback-success {
    display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 30px 18px;
    text-align: center; color: var(--indexaai-muted);
  }
  .indexaai-widget-feedback-success strong { color: var(--indexaai-text); }
  .indexaai-widget-feedback-success svg { width: 28px; height: 28px; color: var(--indexaai-primary); }

  .indexaai-widget-cards-title { margin: 0 0 8px; font-size: 12px; font-weight: 600; color: var(--indexaai-muted); }
  .indexaai-widget-cards {
    display: flex; flex-direction: column; gap: 10px; width: 100%; padding: 0 2px 6px;
    overflow: visible;
  }
  .indexaai-widget-card {
    display: grid; grid-template-columns: 82px minmax(0, 1fr); width: 100%; flex: 0 0 auto;
    overflow: hidden;
    border: 1px solid var(--indexaai-border); border-radius: 16px;
    background: var(--indexaai-background); color: var(--indexaai-text);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  }
  .indexaai-widget-card-media {
    min-height: 96px; background: #ffffff; border-right: 1px solid var(--indexaai-border);
    display: flex; align-items: center; justify-content: center; overflow: hidden;
  }
  .indexaai-widget-card-media img { width: 100%; height: 100%; object-fit: contain; }
  .indexaai-widget-card-media--empty { background: var(--indexaai-subtle); color: var(--indexaai-muted); }
  .indexaai-widget-card-media--empty svg { width: 28px; height: 28px; }
  .indexaai-widget-card-body { display: flex; min-width: 0; flex-direction: column; gap: 7px; padding: 10px; }
  .indexaai-widget-card-name {
    margin: 0; min-height: 3.4rem; font-size: 13px; font-weight: 600; line-height: 1.3;
    display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  }
  .indexaai-widget-live-price { display: block; min-height: 18px; color: var(--indexaai-text); font-size: 12px; font-weight: 650; line-height: 1.45; }
  .indexaai-widget-live-price--loading { display: flex; align-items: center; }
  .indexaai-widget-price-skeleton { display: block; width: 96px; height: 12px; border-radius: 999px; background: color-mix(in srgb, var(--indexaai-muted) 24%, var(--indexaai-background)); }
  .indexaai-widget-category {
    align-self: flex-start; max-width: 100%; padding: 4px 8px; border-radius: 999px;
    background: var(--indexaai-subtle); color: var(--indexaai-muted); font-size: 10px; font-weight: 600;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .indexaai-widget-btn {
    display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; min-height: 36px;
    padding: 8px; border-radius: 8px; border: 1px solid transparent; cursor: pointer;
    font-size: 12px; font-weight: 600; line-height: 1.2; text-decoration: none;
  }
  .indexaai-widget-btn svg { width: 14px; height: 14px; }
  .indexaai-widget-card-link { background: var(--indexaai-primary); color: var(--indexaai-accent-fg); }
  .indexaai-widget-card-ask { background: var(--indexaai-background); color: var(--indexaai-text); border-color: var(--indexaai-border); }
  .indexaai-widget-card-add { background: var(--indexaai-subtle); color: var(--indexaai-text); border-color: var(--indexaai-border); }
  .indexaai-widget-card-actions { display: flex; gap: 6px; }
  .indexaai-widget-card-actions .indexaai-widget-btn { min-width: 0; flex: 1; padding: 6px 4px; font-size: 11px; }
  .indexaai-widget-products-more, .indexaai-widget-sheet-more {
    display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 40px;
    padding: 8px 12px; border: 1px solid var(--indexaai-border); border-radius: 12px; cursor: pointer;
    background: var(--indexaai-background); color: var(--indexaai-primary); font-size: 13px; font-weight: 600;
  }
  .indexaai-widget-products-more:hover, .indexaai-widget-sheet-more:hover { background: var(--indexaai-subtle); }
  .indexaai-widget-products-more svg, .indexaai-widget-sheet-more svg { width: 16px; height: 16px; }
  .indexaai-widget-products-more { margin-top: 8px; }
  .indexaai-widget-sheet-more:disabled { cursor: wait; opacity: 0.65; }

  .indexaai-widget-sheet {
    position: absolute; inset: 0; z-index: 5; display: flex; flex-direction: column;
    background: var(--indexaai-background); color: var(--indexaai-text);
  }
  .indexaai-widget-sheet[hidden] { display: none; }
  .indexaai-widget-sheet-header {
    display: flex; align-items: center; gap: 12px; padding: 16px;
    background: var(--indexaai-primary); color: var(--indexaai-accent-fg); flex-shrink: 0;
  }
  .indexaai-widget-sheet-heading { min-width: 0; flex: 1; }
  .indexaai-widget-sheet-title { margin: 0; color: var(--indexaai-accent-fg); font-size: 16px; font-weight: 650; }
  .indexaai-widget-sheet-subtitle { margin: 4px 0 0; color: var(--indexaai-accent-fg); font-size: 12px; line-height: 1.4; opacity: 0.8; }
  .indexaai-widget-sheet-close {
    display: flex; width: 36px; height: 36px; flex: 0 0 36px; align-items: center; justify-content: center;
    padding: 0; border: 0; border-radius: 999px; cursor: pointer;
    background: rgba(255,255,255,0.16); color: inherit;
  }
  .indexaai-widget-sheet-close:hover { background: rgba(255,255,255,0.28); }
  .indexaai-widget-sheet-close svg { width: 18px; height: 18px; }
  .indexaai-widget-sheet-content { min-height: 0; flex: 1; overflow-y: auto; overscroll-behavior: contain; padding: 14px; }
  .indexaai-widget-sheet-products { display: flex; flex-direction: column; gap: 10px; }
  .indexaai-widget-sheet .indexaai-widget-quote { border: 0; padding: 0; box-shadow: none; }
  .indexaai-widget-sheet .indexaai-widget-quote-head { display: none; }
  .indexaai-widget-sheet-empty, .indexaai-widget-sheet-note {
    margin: 8px 0; color: var(--indexaai-muted); font-size: 13px; line-height: 1.5; text-align: center;
  }
  .indexaai-widget-sheet-note { text-align: left; }

  .indexaai-widget-quote {
    align-self: stretch; padding: 14px; border: 1px solid var(--indexaai-border);
    border-radius: 16px; background: var(--indexaai-background); box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  }
  /* Cabeçalho do orçamento em grade: título e chevron na 1ª linha, contagem na 2ª, texto de apoio embaixo
     ocupando a largura toda. O título fica numa linha só (nowrap). */
  .indexaai-widget-quote-head {
    display: grid; width: 100%; padding: 0; border: none; background: none; color: inherit; text-align: left; cursor: pointer;
    grid-template-columns: 40px minmax(0, 1fr) 16px;
    grid-template-areas: "icon title chevron" "icon count chevron" "help help help";
    column-gap: 12px; row-gap: 4px; align-items: center;
  }
  .indexaai-widget-quote-icon {
    grid-area: icon; align-self: start;
    width: 40px; height: 40px; flex-shrink: 0; border-radius: 12px; color: var(--indexaai-primary);
    display: flex; align-items: center; justify-content: center; background: var(--indexaai-accent-bg);
  }
  .indexaai-widget-quote-icon svg { width: 20px; height: 20px; }
  .indexaai-widget-quote-titles { display: contents; }
  .indexaai-widget-quote-title {
    grid-area: title; min-width: 0; font-size: 14px; font-weight: 600;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .indexaai-widget-quote-sub { grid-area: help; font-size: 12px; color: var(--indexaai-muted); }
  .indexaai-widget-quote-count {
    grid-area: count; justify-self: start; padding: 4px 10px; border-radius: 999px;
    font-size: 12px; font-weight: 600; white-space: nowrap;
    background: var(--indexaai-accent-bg); color: var(--indexaai-primary);
  }
  .indexaai-widget-quote-chevron { grid-area: chevron; color: var(--indexaai-muted); display: flex; }
  .indexaai-widget-quote-chevron svg { width: 16px; height: 16px; }
  .indexaai-widget-quote-list { list-style: none; margin: 12px 0 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
  .indexaai-widget-quote-item {
    display: flex; align-items: center; gap: 10px; padding: 8px; border-radius: 12px;
    border: 1px solid var(--indexaai-border); background: var(--indexaai-subtle);
  }
  .indexaai-widget-quote-thumb {
    width: 56px; height: 56px; flex-shrink: 0; overflow: hidden; border-radius: 8px;
    border: 1px solid var(--indexaai-border); background: #ffffff;
    display: flex; align-items: center; justify-content: center;
  }
  .indexaai-widget-quote-thumb img { width: 100%; height: 100%; object-fit: contain; }
  .indexaai-widget-quote-thumb--empty { background: var(--indexaai-accent-bg); color: var(--indexaai-muted); }
  .indexaai-widget-quote-thumb svg { width: 20px; height: 20px; }
  .indexaai-widget-quote-info { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 4px; }
  .indexaai-widget-quote-name {
    font-size: 12px; font-weight: 600; line-height: 1.35;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .indexaai-widget-quote-qty {
    align-self: flex-start; padding: 2px 8px; border-radius: 999px; font-size: 11px;
    background: var(--indexaai-background); color: var(--indexaai-muted);
  }
  .indexaai-widget-quote-total { margin: 12px 0 0; padding-top: 12px; border-top: 1px solid var(--indexaai-border); font-size: 14px; font-weight: 700; }
  .indexaai-widget-quote-link {
    display: inline-flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 500;
    color: var(--indexaai-primary); text-decoration: none;
  }
  .indexaai-widget-quote-link:hover { text-decoration: underline; }
  .indexaai-widget-quote-link svg { width: 12px; height: 12px; }
  .indexaai-widget-quote-remove {
    width: 36px; height: 36px; flex-shrink: 0; padding: 0; border: none; border-radius: 999px; cursor: pointer;
    display: flex; align-items: center; justify-content: center; background: transparent; color: var(--indexaai-muted);
  }
  .indexaai-widget-quote-remove:hover { background: #fef2f2; color: #dc2626; }
  .indexaai-widget-quote-remove svg { width: 16px; height: 16px; }
  .indexaai-widget-quote-foot {
    margin: 12px 0 0; padding-top: 12px; border-top: 1px solid var(--indexaai-border);
    font-size: 12px; line-height: 1.6; color: var(--indexaai-muted);
  }

  .indexaai-widget-attach-row {
    display: flex; align-items: center; gap: 8px; margin: 0 12px; padding: 8px;
    border-radius: 12px; background: var(--indexaai-subtle); flex-shrink: 0;
  }
  .indexaai-widget-attach-thumb { width: 48px; height: 48px; flex-shrink: 0; border-radius: 8px; object-fit: cover; background: #ffffff; }
  .indexaai-widget-attach-info { min-width: 0; flex: 1; }
  .indexaai-widget-attach-name { display: block; font-size: 12px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .indexaai-widget-attach-note { display: block; font-size: 11px; color: var(--indexaai-muted); }
  .indexaai-widget-attach-remove {
    width: 32px; height: 32px; flex-shrink: 0; padding: 0; border: none; border-radius: 8px; cursor: pointer;
    display: flex; align-items: center; justify-content: center; background: transparent; color: var(--indexaai-text);
  }
  .indexaai-widget-attach-remove svg { width: 16px; height: 16px; }
  .indexaai-widget-status { margin: 6px 16px 0; font-size: 12px; color: #b91c1c; flex-shrink: 0; }
  .indexaai-widget-status:empty { display: none; }

  .indexaai-widget-form {
    display: flex; align-items: center; gap: 8px; padding: 12px;
    border-top: 1px solid var(--indexaai-border); flex-shrink: 0;
  }
  /* Linha do campo: tudo com 40 px, centralizado, mesmo espaçamento; a altura não muda com o foco */
  .indexaai-widget-attach {
    width: 40px; height: 40px; flex: 0 0 40px; padding: 0; border: none; border-radius: 999px; cursor: pointer;
    display: flex; align-items: center; justify-content: center; background: transparent; color: var(--indexaai-muted);
  }
  .indexaai-widget-attach:hover { background: var(--indexaai-subtle); color: var(--indexaai-text); }
  .indexaai-widget-attach svg { width: 18px; height: 18px; }
  .indexaai-widget-input {
    flex: 1 1 auto; min-width: 0; height: 40px; margin: 0; padding: 0 16px;
    border: 1px solid var(--indexaai-border); border-radius: 999px; background: var(--indexaai-subtle);
    font-size: 14px; color: inherit;
  }
  .indexaai-widget-input::placeholder { color: var(--indexaai-muted); opacity: 1; }
  .indexaai-widget-send {
    width: 40px; height: 40px; flex: 0 0 40px; padding: 0; border: none; border-radius: 999px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    background: var(--indexaai-primary); color: var(--indexaai-accent-fg);
  }
  .indexaai-widget-send svg { width: 16px; height: 16px; }
  .indexaai-widget-quote-button {
    position: relative; width: 40px; height: 40px; flex: 0 0 40px; padding: 0; border: 1px solid var(--indexaai-border);
    border-radius: 999px; cursor: pointer; display: flex; align-items: center; justify-content: center;
    background: var(--indexaai-background); color: var(--indexaai-primary);
  }
  .indexaai-widget-quote-button[hidden] { display: none; }
  .indexaai-widget-quote-button:hover { background: var(--indexaai-subtle); }
  .indexaai-widget-quote-button > svg { width: 18px; height: 18px; }
  .indexaai-widget-quote-badge {
    position: absolute; top: -3px; right: -3px; min-width: 16px; height: 16px; padding: 0 3px;
    border: 1px solid var(--indexaai-background); border-radius: 999px; display: flex; align-items: center; justify-content: center;
    background: var(--indexaai-primary); color: var(--indexaai-accent-fg); font-size: 9px; font-weight: 700;
  }
  .indexaai-widget-footer {
    display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 6px 16px 10px;
    border-top: 1px solid var(--indexaai-border); flex-shrink: 0; color: var(--indexaai-muted); font-size: 11px;
  }
  .indexaai-widget-feedback-link, .indexaai-widget-powered-link {
    padding: 0; border: 0; background: transparent; color: inherit; cursor: pointer; text-decoration: none;
  }
  .indexaai-widget-feedback-link:hover, .indexaai-widget-powered-link:hover { color: var(--indexaai-primary); text-decoration: underline; }
  .indexaai-widget-powered-link { font-weight: 600; white-space: nowrap; }

  @media (prefers-reduced-motion: no-preference) {
    @keyframes indexaai-panel-in {
      from { opacity: 0; transform: translateY(10px) scale(0.96); }
      to { opacity: 1; transform: none; }
    }
    @keyframes indexaai-panel-out {
      from { opacity: 1; transform: none; }
      to { opacity: 0; transform: translateY(8px) scale(0.96); }
    }
    @keyframes indexaai-msg-in {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: none; }
    }
    @keyframes indexaai-card-in {
      from { opacity: 0; transform: translateY(8px) scale(0.98); }
      to { opacity: 1; transform: none; }
    }
    @keyframes indexaai-price-shimmer {
      from { opacity: 0.55; }
      to { opacity: 1; }
    }
    @keyframes indexaai-dot {
      0%, 80%, 100% { opacity: 0.35; transform: translateY(0); }
      40% { opacity: 1; transform: translateY(-3px); }
    }

    .indexaai-widget-panel:not([hidden]) { animation: indexaai-panel-in 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
    .indexaai-widget-panel.is-closing { animation: indexaai-panel-out 160ms ease-in both; }
    .indexaai-widget-button { transition: transform 180ms ease, box-shadow 180ms ease; }
    .indexaai-widget-button:hover { transform: translateY(-2px) scale(1.04); box-shadow: 0 10px 24px rgba(15, 23, 42, 0.26); }
    .indexaai-widget-button:active { transform: scale(0.96); }
    .indexaai-widget-message { animation: indexaai-msg-in 240ms ease-out both; }
    .indexaai-widget-card { animation: indexaai-card-in 320ms ease-out both; animation-delay: calc(var(--indexaai-i, 0) * 70ms); }
    .indexaai-widget-price-skeleton { animation: indexaai-price-shimmer 900ms ease-in-out infinite alternate; }
    .indexaai-widget-dot { animation: indexaai-dot 1.1s ease-in-out infinite; }
    .indexaai-widget-dot:nth-child(2) { animation-delay: 0.15s; }
    .indexaai-widget-dot:nth-child(3) { animation-delay: 0.3s; }
    .indexaai-widget-chip { transition: background-color 160ms ease, transform 160ms ease; }
    .indexaai-widget-chip:hover { transform: translateY(-1px); }
    .indexaai-widget-btn { transition: filter 160ms ease; }
    .indexaai-widget-btn:hover { filter: brightness(0.95); }
    .indexaai-widget-quote-chevron svg { transition: transform 180ms ease; }
    .indexaai-widget-quote-chevron[data-open="false"] svg { transform: rotate(-90deg); }
  }

  @media (prefers-reduced-motion: reduce) {
    .indexaai-widget *, .indexaai-widget *::before, .indexaai-widget *::after {
      animation: none !important; transition: none !important;
    }
  }
`;

export function injectWidgetStyles(shadowRoot: ShadowRoot): void {
  const style = document.createElement("style");
  style.id = WIDGET_STYLE_ID;
  style.textContent = CSS;
  shadowRoot.appendChild(style);
}
